// Week view — aggregate stats + day-by-day mini ribbons + observations.
// PRD §5.8.

import { useMemo } from "react";
import type { EventRow } from "../lib/events";
import { sleepEndMs, startOfLocalDay } from "../lib/events";
import { daytimeSleepMinutesForDay } from "../lib/schedule";
import { minutesFromMidnight, durStr } from "../lib/timeFmt";
import { useNavigate } from "react-router-dom";
import { useT, useAmount } from "../lib/LocaleContext";
import type { Strings } from "../lib/i18n";

type Props = {
  babyId: string;
  events: EventRow[];
};

type DayRow = {
  date: Date;
  label: string;
  intakeOz: number;
  daytimeSleepMin: number;
  longestStretchMin: number;
  segments: Segment[];
};

type Segment = { kind: "feed" | "sleep"; startMin: number; durMin: number };

export function HistoryView({ events }: Props) {
  const navigate = useNavigate();
  const t = useT();
  const amount = useAmount();
  const days = useMemo(() => buildLastNDays(events, 7, t), [events, t]);
  const agg = useMemo(() => aggregate(days), [days]);
  const observations = useMemo(() => observe(days, t), [days, t]);

  return (
    <div className="bh-root">
      <div style={styles.head}>
        <button style={styles.back} onClick={() => navigate(-1)} aria-label={t.history.back}>
          ←
        </button>
        <div style={styles.title}>{t.history.title}</div>
        <div style={{ width: 44 }} />
      </div>

      <div className="bh-scroll">
        <div style={styles.aggRow}>
          <Stat label={t.history.avgIntake} big={agg.avgIntakeOz != null ? amount.fmt(agg.avgIntakeOz) : "—"} />
          <Stat label={t.history.avgDaytimeSleep} big={agg.avgDaytimeSleepMin > 0 ? durStr(agg.avgDaytimeSleepMin) : "—"} />
          <Stat label={t.history.avgLongestStretch} big={agg.avgLongestStretchMin > 0 ? durStr(agg.avgLongestStretchMin) : "—"} />
        </div>

        <div style={styles.section}>
          <div style={styles.sectionLabel}>{t.history.dayByDay}</div>
          <div style={styles.list}>
            {days.map((d, i) => (
              <button
                key={d.label}
                style={{ ...styles.dayRow, borderBottom: i === days.length - 1 ? "0" : "1px solid var(--bh-divider)" }}
                onClick={() => navigate(`/b/${event_babyId(events)}/?day=${ymd(d.date)}`)}
              >
                <div style={styles.dayHead}>
                  <span style={styles.dayLabel}>{d.label}</span>
                  <span style={styles.dayDate}>
                    {d.date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                  </span>
                </div>
                <MiniRibbon segments={d.segments} />
                <div style={styles.dayStats}>
                  <span className="bh-num">{d.intakeOz > 0 ? amount.fmt(d.intakeOz) : "—"}</span>
                  <span style={styles.statDot}>·</span>
                  <span className="bh-num">{d.daytimeSleepMin > 0 ? durStr(d.daytimeSleepMin) : t.history.noSleep}</span>
                  <span style={styles.statDot}>·</span>
                  <span className="bh-num">{d.longestStretchMin > 0 ? t.history.overnight(durStr(d.longestStretchMin)) : t.history.noOvernight}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {observations.length > 0 && (
          <div style={styles.section}>
            <div style={styles.sectionLabel}>{t.history.whatWeNoticed}</div>
            <div style={styles.obsCard}>
              {observations.map((o, i) => (
                <div key={i} style={{ ...styles.obsItem, borderTop: i === 0 ? "0" : "1px solid var(--bh-divider)" }}>
                  {o}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MiniRibbon({ segments }: { segments: Segment[] }) {
  if (segments.length === 0) {
    return <div style={{ ...styles.ribbon, opacity: 0.35 }} />;
  }
  const dayStart = 6 * 60;
  const dayEnd = 22 * 60;
  const total = dayEnd - dayStart;
  return (
    <div style={styles.ribbon}>
      {segments.map((s, i) => {
        const start = Math.max(s.startMin, dayStart);
        const end = Math.min(s.startMin + s.durMin, dayEnd);
        if (end <= start) return null;
        const left = ((start - dayStart) / total) * 100;
        const w = Math.max(0.8, ((end - start) / total) * 100);
        const color = s.kind === "feed" ? "var(--bh-feed)" : "var(--bh-sleep)";
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: `${left}%`,
              width: `${w}%`,
              top: 2,
              bottom: 2,
              background: color,
              borderRadius: 2,
            }}
          />
        );
      })}
    </div>
  );
}

function Stat({ label, big }: { label: string; big: string }) {
  return (
    <div style={styles.stat}>
      <div style={styles.statLabel}>{label}</div>
      <div className="bh-num" style={styles.statBig}>
        {big}
      </div>
    </div>
  );
}

function buildLastNDays(events: EventRow[], n: number, t: Strings): DayRow[] {
  const rows: DayRow[] = [];
  const today = startOfLocalDay();
  for (let i = 0; i < n; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    const dayStart = date.getTime();
    const dayEnd = dayStart + 86_400_000;
    let intakeOz = 0;
    // Same 7am–7pm clipped-window rule as the Today screen's guidance strip.
    const daytimeSleepMin = daytimeSleepMinutesForDay(events, date);
    const segments: Segment[] = [];
    for (const e of events) {
      const t = new Date(e.at).getTime();
      if (t < dayStart || t >= dayEnd) continue;
      if (e.type === "feed" && e.amount_oz != null) intakeOz += e.amount_oz;
      if (e.type === "feed" || e.type === "nurse") {
        const startMin = minutesFromMidnight(new Date(e.at));
        segments.push({ kind: "feed", startMin, durMin: e.duration_min ?? 12 });
      }
      if (e.type === "sleep") {
        const start = new Date(e.at);
        // A sleep still in progress counts up to now, same as the Today screen —
        // it used to be skipped outright, so an ongoing nap left today's row blank.
        const durMin = Math.round((sleepEndMs(e) - start.getTime()) / 60_000);
        if (durMin <= 0) continue;
        const startMin = minutesFromMidnight(start);
        segments.push({ kind: "sleep", startMin, durMin });
      }
    }
    const longestStretchMin = longestNightStretch(events, date);
    rows.push({
      date,
      label: i === 0 ? t.history.today : i === 1 ? t.history.yesterday : date.toLocaleDateString(undefined, { weekday: "long" }),
      intakeOz,
      daytimeSleepMin,
      longestStretchMin,
      segments,
    });
  }
  return rows;
}

function longestNightStretch(events: EventRow[], date: Date): number {
  const nightStart = new Date(date);
  nightStart.setHours(19, 0, 0, 0);
  const nightEnd = new Date(nightStart);
  nightEnd.setDate(nightEnd.getDate() + 1);
  nightEnd.setHours(7, 0, 0, 0);
  const feeds = events
    .filter((e) => e.type === "feed" || e.type === "nurse")
    .map((e) => new Date(e.at).getTime())
    .filter((t) => t >= nightStart.getTime() && t < nightEnd.getTime())
    .sort((a, b) => a - b);
  let longest = 0;
  const boundaries = [nightStart.getTime(), ...feeds, nightEnd.getTime()];
  for (let i = 1; i < boundaries.length; i++) {
    const gap = boundaries[i] - boundaries[i - 1];
    if (gap > longest) longest = gap;
  }
  return Math.round(longest / 60_000);
}

function aggregate(days: DayRow[]): { avgIntakeOz: number | null; avgDaytimeSleepMin: number; avgLongestStretchMin: number } {
  if (days.length === 0) return { avgIntakeOz: null, avgDaytimeSleepMin: 0, avgLongestStretchMin: 0 };
  const intakeDays = days.filter((d) => d.intakeOz > 0);
  return {
    avgIntakeOz: intakeDays.length ? intakeDays.reduce((s, d) => s + d.intakeOz, 0) / intakeDays.length : null,
    avgDaytimeSleepMin: Math.round(days.reduce((s, d) => s + d.daytimeSleepMin, 0) / days.length),
    avgLongestStretchMin: Math.round(days.reduce((s, d) => s + d.longestStretchMin, 0) / days.length),
  };
}

function observe(days: DayRow[], t: Strings): string[] {
  const obs: string[] = [];
  if (days.length >= 3) {
    const recent = days[0].longestStretchMin;
    const older = days[days.length - 1].longestStretchMin;
    if (recent > 0 && older > 0 && recent > older + 60) {
      obs.push(t.history.obsStretchLengthened(Math.round(older / 60), Math.round(recent / 60)));
    }
  }
  return obs;
}

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function event_babyId(events: EventRow[]): string {
  return events[0]?.baby_id ?? "";
}

const styles: Record<string, React.CSSProperties> = {
  head: {
    display: "flex",
    alignItems: "center",
    padding: "8px 14px 14px",
    gap: 8,
  },
  back: {
    width: 44,
    height: 44,
    borderRadius: 999,
    background: "var(--bh-surface)",
    border: "none",
    color: "var(--bh-ink-2)",
    cursor: "pointer",
    fontSize: 18,
  },
  title: { flex: 1, textAlign: "center", fontSize: 18, fontWeight: 600, color: "var(--bh-ink)" },
  aggRow: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, margin: "0 14px 18px" },
  stat: { background: "var(--bh-surface)", borderRadius: 16, padding: 12 },
  statLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "var(--bh-ink-3)",
    marginBottom: 4,
  },
  statBig: { fontSize: 18, fontWeight: 600, color: "var(--bh-ink)", lineHeight: 1 },
  section: { margin: "0 14px 16px" },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "var(--bh-ink-3)",
    marginBottom: 8,
    padding: "0 4px",
  },
  list: { background: "var(--bh-surface)", borderRadius: 20, padding: "4px 16px" },
  dayRow: {
    width: "100%",
    appearance: "none",
    background: "transparent",
    border: "none",
    color: "inherit",
    font: "inherit",
    textAlign: "left",
    cursor: "pointer",
    padding: "14px 0",
  },
  dayHead: { display: "flex", alignItems: "baseline", gap: 8 },
  dayLabel: { fontSize: 16, fontWeight: 600, color: "var(--bh-ink)" },
  dayDate: { fontSize: 13, color: "var(--bh-ink-3)" },
  ribbon: {
    position: "relative",
    height: 14,
    background: "var(--bh-surface-press)",
    borderRadius: 4,
    margin: "8px 0",
    overflow: "hidden",
  },
  dayStats: { fontSize: 13, color: "var(--bh-ink-3)", display: "flex", gap: 6, alignItems: "center" },
  statDot: { color: "var(--bh-ink-4)" },
  obsCard: { background: "var(--bh-surface)", borderRadius: 20, padding: "4px 16px" },
  obsItem: { padding: "14px 0", fontSize: 15, color: "var(--bh-ink-2)", lineHeight: 1.5 },
};
